import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select, { components as defaultComponents } from 'react-select';
import { Icon } from '@iconify/react';
import {
  enhanceHierarchyOptions,
  flattenHierarchy,
  getExpandableNodeIds,
  getHierarchyAncestorIds,
} from '../../utils/hierarchy';

const Option = (props) => {
  const { data, innerRef, selectProps } = props;
  const { onMouseDown, ...restInnerProps } = props.innerProps;
  const hierarchyContext = selectProps?.hierarchy || {};
  const canToggle = hierarchyContext.canToggle !== false;
  const toggleNode = hierarchyContext.toggleNode;
  const onOptionToggle = hierarchyContext.onOptionToggle;
  const selectionState = hierarchyContext.selectionState || {};
  const isMulti = Boolean(selectProps?.isMulti);
  const isDisabled = Boolean(props.isDisabled);
  const optionState = selectionState[data.value] || {};
  const isSelected = optionState.isSelected ?? Boolean(props.isSelected);
  const isIndeterminate = Boolean(optionState.isIndeterminate);
  const checkboxRef = useRef(null);

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isIndeterminate;
    }
  }, [isIndeterminate]);

  const handleExpandClick = useCallback(
    (event) => {
      if (!data.isParent || !canToggle || typeof toggleNode !== 'function') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      toggleNode(data.value);
    },
    [canToggle, data.isParent, data.value, toggleNode],
  );

  const handleExpandMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleCheckboxMouseDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleCheckboxClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleCheckboxChange = useCallback(
    (event) => {
      if (!isMulti || typeof onOptionToggle !== 'function' || isDisabled) {
        return;
      }
      event.stopPropagation();
      onOptionToggle(data.value, event.target.checked);
    },
    [data.value, isDisabled, isMulti, onOptionToggle],
  );

  const handleMouseDown = useCallback(
    (event) => {
      if (isDisabled) {
        event.preventDefault();
        event.stopPropagation();
         return;
      }

      const target = event.target;
      if (typeof Element !== 'undefined' && target instanceof Element) {
        if (target.closest('button') || target.closest('input')) {
          if (typeof onMouseDown === 'function') {
            onMouseDown(event);
          }
          return;
        }
      }

      if (isMulti && typeof onOptionToggle === 'function') {
        event.preventDefault();
        event.stopPropagation();
        onOptionToggle(data.value, !isSelected);
        return;
      }
      if (typeof onMouseDown === 'function') {
        onMouseDown(event);
      }
    },
    [data.value, isDisabled, isMulti, isSelected, onMouseDown, onOptionToggle],
  );

  const parentPath = Array.isArray(data.pathLabel)
    ? data.pathLabel.slice(0, -1).filter(Boolean).join(' › ')
    : '';

  return (
    <defaultComponents.Option
      {...props}
      innerProps={{ ...restInnerProps, onMouseDown: handleMouseDown }}
      innerRef={innerRef}
    >
      <div className="d-flex align-items-center justify-content-between w-100">
        <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
          {data.isParent ? (
            <button
              type="button"
              className="btn btn-sm p-0 border-0 bg-transparent text-muted d-flex align-items-center justify-content-center"
              style={{ width: 20, height: 20 }}
              onClick={handleExpandClick}
              onMouseDown={handleExpandMouseDown}
              disabled={!canToggle || isDisabled}
              aria-label={data.isExpanded ? 'Collapse' : 'Expand'}
              aria-expanded={data.isExpanded}
            >
              <Icon
                icon={data.isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'}
                className="text-lg"
              />
            </button>
          ) : (
            <span style={{ width: 20 }} />
          )}
          {isMulti && (
            <span className="d-flex align-items-center" style={{ width: 20 }}>
              <input
                ref={checkboxRef}
                type="checkbox"
                className="form-check-input m-0"
                checked={!!isSelected}
                onChange={handleCheckboxChange}
                onMouseDown={handleCheckboxMouseDown}
                onClick={handleCheckboxClick}
                disabled={isDisabled}
              />
            </span>
          )}
          <span className="text-truncate">{data.label}</span>
        </div>
        {parentPath && (
          <span className="text-muted small text-end" style={{ maxWidth: '50%' }}>
            {parentPath}
          </span>
        )}
      </div>
    </defaultComponents.Option>
  );
};

export const HierarchySelect = ({
  options = [],
  tree,
  value,
  onChange,
  isMulti = true,
  styles,
  components,
  classNamePrefix = 'react-select',
  menuPortalTarget,
  ...rest
}) => {
  const { tree: builtTree } = useMemo(() => {
    if (tree) {
      return { tree };
    }
    return enhanceHierarchyOptions(options);
  }, [options, tree]);

  const hierarchyNodes = builtTree?.nodesById ?? {};
  const roots = builtTree?.roots ?? [];
  const expandableIds = useMemo(() => getExpandableNodeIds(hierarchyNodes), [hierarchyNodes]);

  const [expanded, setExpanded] = useState(() => new Set());
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    setExpanded((prev) => {
      const valid = new Set(Object.values(hierarchyNodes).map((node) => node.id));
      const next = new Set();
      prev.forEach((id) => {
        if (valid.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [hierarchyNodes]);

  useEffect(() => {
    const ids = Array.isArray(value)
      ? value
      : value !== undefined && value !== null
        ? [value]
        : [];
    if (!ids.length) {
      return;
    }
    setExpanded((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        getHierarchyAncestorIds(hierarchyNodes, id).forEach((ancestorId) => {
          next.add(ancestorId);
        });
      });
      return next;
    });
  }, [hierarchyNodes, value]);

  const toggleNode = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const visibleOptions = useMemo(
    () => flattenHierarchy(roots, expanded),
    [roots, expanded],
  );

  const fullyExpandedOptions = useMemo(() => flattenHierarchy(roots, expandableIds), [roots, expandableIds]);

  const displayedOptions = inputValue ? fullyExpandedOptions : visibleOptions;

  const multiValue = useMemo(() => {
    if (!isMulti) {
      return [];
    }
    const ids = Array.isArray(value) ? value : [];
    const unique = [];
    const seen = new Set();
    ids.forEach((id) => {
      if (!hierarchyNodes[id] || seen.has(id)) {
        return;
      }
      seen.add(id);
      unique.push(id);
    });
    return unique;
  }, [hierarchyNodes, isMulti, value]);
  
  const selectedOptions = useMemo(() => {
    if (isMulti) {
      return multiValue.map((id) => hierarchyNodes[id]?.option).filter(Boolean);
    }
    if (value === undefined || value === null || value === '') {
      return null;
    }
    return hierarchyNodes[value]?.option ?? null;
  }, [hierarchyNodes, isMulti, multiValue, value]);

  const selectionState = useMemo(() => {
    if (!isMulti) {
      return {};
    }
    const selectedSet = new Set(multiValue);
    const state = {};

    const visit = (node) => {
      const childStates = node.children.map(visit);
      const isSelected = selectedSet.has(node.id);
      const hasChildren = node.children.length > 0;
      const allChildrenFullySelected =
        childStates.length > 0 && childStates.every((child) => child.fullySelected);
      const anyChildSelected = childStates.some((child) => child.anySelected);
      const fullySelected = hasChildren ? isSelected && allChildrenFullySelected : isSelected;
      const anySelected = isSelected || anyChildSelected;
      const isIndeterminate = hasChildren && anySelected && !fullySelected;
      const current = { isSelected, fullySelected, anySelected, isIndeterminate };
      state[node.id] = current;
      return current;
    };

    roots.forEach(visit);
    return state;
  }, [isMulti, multiValue, roots]);

  const cascadeSelection = useCallback(
    (node, shouldSelect, baseValues) => {
      const baseArray = baseValues
        ? Array.isArray(baseValues)
          ? baseValues
          : Array.from(baseValues)
        : multiValue;
      const workingSet = new Set(baseArray);

      if (node) {
        const updateBranch = (current) => {
          if (!current) {
            return;
          }
          if (shouldSelect) {
            workingSet.add(current.id);
          } else {
            workingSet.delete(current.id);
          }
          current.children.forEach((child) => updateBranch(child));
        };

        updateBranch(node);
      }

      return workingSet;
    },
    [multiValue],
  );

  const handleOptionToggle = useCallback(
    (id, shouldSelect) => {
      if (!isMulti) {
        return;
      }
      const node = hierarchyNodes[id];
      if (!node) {
        const workingSet = new Set(multiValue);
        if (shouldSelect) {
          workingSet.add(id);
        } else {
          workingSet.delete(id);
        }
        onChange?.(Array.from(workingSet));
        return;
      }
      const nextSet = cascadeSelection(node, shouldSelect);
      onChange?.(Array.from(nextSet));
    },
    [cascadeSelection, hierarchyNodes, isMulti, multiValue, onChange],
  );

  const handleChange = useCallback(
    (selected, actionMeta) => {
      if (!isMulti) {
        onChange?.(selected ? selected.value : null);
        return;
      }

      const action = actionMeta?.action;

      if (action === 'clear') {
        onChange?.([]);
        return;
      }
      
      if ((action === 'remove-value' || action === 'pop-value') && actionMeta?.removedValue) {
        const node = hierarchyNodes[actionMeta.removedValue.value];
        if (node) {
          const nextSet = cascadeSelection(node, false);
          onChange?.(Array.from(nextSet));
        } else {
          const workingSet = new Set(multiValue);
          workingSet.delete(actionMeta.removedValue.value);
          onChange?.(Array.from(workingSet));
        }
        return;
      }

      if ((action === 'select-option' || action === 'deselect-option') && actionMeta?.option) {
        const node = hierarchyNodes[actionMeta.option.value];
        if (node) {
          const nextSet = cascadeSelection(node, action === 'select-option');
          onChange?.(Array.from(nextSet));
          return;
        }
      }

      const next = Array.isArray(selected) ? selected.map((opt) => opt.value) : [];
      onChange?.(next);
    },
    [cascadeSelection, hierarchyNodes, isMulti, multiValue, onChange],
  );

  const handleInputChange = useCallback((nextValue, actionMeta) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(nextValue);
    }
    if (actionMeta.action === 'menu-close') {
      setInputValue('');
    }
    return nextValue;
  }, []);

  const mergedComponents = useMemo(
    () => ({
      ...components,
      Option,
    }),
    [components],
  );

  const mergedStyles = useMemo(() => {
    const mergeStyle = (styleProp, provided, state) => {
      if (typeof styleProp === 'function') {
        return styleProp(provided, state);
      }
      if (styleProp && typeof styleProp === 'object') {
        return { ...provided, ...styleProp };
      }
      return provided;
    };

    return {
      ...styles,
      option: (provided, state) => {
        const base = mergeStyle(styles?.option, provided, state);
        const depth = state.data?.depth ?? 0;
        return {
          ...base,
          paddingLeft: 12 + depth * 16,
        };
      },
      menuPortal: (provided, state) => {
        const base = mergeStyle(styles?.menuPortal, provided, state);
        const baseZIndex = Number(base?.zIndex);
        return {
          ...base,
          zIndex: Number.isFinite(baseZIndex) ? Math.max(baseZIndex, 99999) : 99999,
        };
      },
      menu: (provided, state) => {
        const base = mergeStyle(styles?.menu, provided, state);
        const baseZIndex = Number(base?.zIndex);
        return {
          ...base,
          zIndex: Number.isFinite(baseZIndex) ? Math.max(baseZIndex, 99999) : 99999,
        };
      },
    };
  }, [styles]);

  const resolvedMenuTarget = menuPortalTarget || (typeof document !== 'undefined' ? document.body : undefined);
  const resolvedMenuPosition = resolvedMenuTarget ? 'fixed' : 'absolute';

  return (
    <Select
      {...rest}
      classNamePrefix={classNamePrefix}
      isMulti={isMulti}
      options={displayedOptions}
      value={selectedOptions}
      onChange={handleChange}
      components={mergedComponents}
      styles={mergedStyles}
      closeMenuOnSelect={!isMulti}
      hideSelectedOptions={false}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      filterOption={(candidate, rawInput) => {
        if (!rawInput) return true;
        const haystack = (candidate.data?.fullLabel || candidate.label || '').toLowerCase();
        return haystack.includes(rawInput.toLowerCase());
      }}
      getOptionValue={(opt) => String(opt.value)}
      formatOptionLabel={(opt, { context }) =>
        context === 'value' ? opt.fullLabel || opt.label : opt.label}
      hierarchy={{
        toggleNode,
        canToggle: !inputValue,
        onOptionToggle: isMulti ? handleOptionToggle : undefined,
        selectionState: isMulti ? selectionState : undefined,
      }}
      menuPortalTarget={resolvedMenuTarget}
      menuPosition={resolvedMenuPosition}
    />
  );
};

export default HierarchySelect;
